

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as RoutesDefinitions from 'apps/console/src/app/routes-definitions';
import { map, tap, switchMap, withLatestFrom, first, catchError, filter, mergeMap } from 'rxjs/operators';
import { Injectable, Injector, inject } from '@angular/core';
import { ValidationService, BrowserNavigationService, scrollToTop, ActionType, NotificationService, LanguageService, FactorConfigurationService } from '@aston/foundation';
import { Router } from '@angular/router';
import { AppConfiguration } from 'apps/console/src/app/app.configuration';
import { from, EMPTY, of, Observable } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent } from 'apps/console/src/app/shared-module/components/confirm-modal/confirm-modal.component';
import { AppConstants } from 'apps/console/src/app/app.constants';
import { UserService } from 'apps/console/src/app/user-module/services';
import { DocumentFileService } from 'apps/console/src/app/shared-module/services/document-file.service';
import { DocumentFilesViewerComponent } from 'apps/console/src/app/shared-module/components/document-files-viewer/document-files-viewer.component';
import { ChoicesModalComponent } from 'apps/console/src/app/shared-module/components/choices-modal/choices-modal.component';
import { IChoiceAction } from 'apps/console/src/app/shared-module/models';

import * as subsidiariesActions from '../subsidiaries-store/actions';

import * as featureSelectors from './selectors';
import * as featureActions from './actions';

@Injectable({providedIn: 'root'})
export class AppStoreEffects {
	private router = inject(Router);
	private actions$ = inject(Actions);
	private store = inject(Store);
	private appConfig = inject(AppConfiguration);
	private validationService = inject(ValidationService);
	private documentFileService = inject(DocumentFileService);
	private userService = inject(UserService);
	private browserNav = inject(BrowserNavigationService);
	private languageService = inject(LanguageService);
	private translateService = inject(TranslateService);
	private modalService = inject(NgbModal);
	private injector = inject(Injector);
	private notificationService = inject(NotificationService);
	private factorConfigService = inject(FactorConfigurationService);


	errorLog$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ErrorLog),
		tap(action => {
			console.error(action.error);
			// FIXME: TelemetryService cannot be injected directly yet
			// this.injector.get(TelemetryService).error(action.error, action.data);
		})
	), { dispatch: false });

	infoLog$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.InfoLog),
		tap(action => {
			console.info(action.message);  
			// FIXME: TelemetryService cannot be injected directly yet
			// this.injector.get(TelemetryService).event(action.message, action.data);
		})
	), { dispatch: false });

	startInitializer$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.StartInitializer),
		switchMap(_ => [
			// plug here config-independant actions
			featureActions.UpdateConfigRequest(),
		])
	));

	loadFactorConfiguration$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadFactorConfigRequest),
		switchMap(_ => this.factorConfigService.getFactorConfig().pipe(
			map(entity => featureActions.LoadFactorConfigSuccess({entity})),
			catchError(error => of(featureActions.LoadFactorConfigFailure({error})))
		))
	));

	loadValidators$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadValidatorsRequest),
		switchMap(_ => this.validationService.loadValidationRuleset()),
		map(_ => featureActions.LoadValidatorsSuccess()),
		catchError(error => of(featureActions.CriticalFailure({ error })))
	));

	onCriticalFailure$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CriticalFailure),
		map(action => {
			console.error(`Encountered a critical error, the page will be reloaded.`, action.error);
			setTimeout(_ => location.reload(), AppConstants.CRITICAL_ERROR_RELOAD_TIMEOUT)
		})
	), { dispatch: false });

	onNotFound$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NotFound),
		tap(context => this.router.navigateByUrl('/', {skipLocationChange: true, state: {context}}))
	), {dispatch: false});

	updateConfig$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateConfigRequest),
		switchMap(_ => from(this.appConfig.ensureInit())),
		tap(config => {
			if (config.defaultLang) {
				this.languageService.setCurrentLanguage(config.defaultLang)
			}
		}),
		switchMap(_ => [
			featureActions.UpdateConfigSuccess({ config: this.appConfig }),
			// plug here config-dependant pre-user actions
			featureActions.LoadFactorConfigRequest(),
			featureActions.LoadValidatorsRequest(),
		])
	));

	checkInitializers$ = createEffect(() => this.actions$.pipe(
		ofType(
			featureActions.UpdateConfigSuccess,
			featureActions.LoadValidatorsSuccess,
			featureActions.LoadFactorConfigSuccess,
		),
		withLatestFrom(this.store.select(featureSelectors.selectState)),
		switchMap(([_, state]) => {
			const configIsPresent = !!state.config;
			const factorConfigIsLoaded = !!state.factorConfig.entity?.countries;

			// add pre-user additional checks here
			if (configIsPresent && factorConfigIsLoaded) {
				return [
					featureActions.FinishInitializer(),
				];
			}
			return EMPTY;
		})
	));

	startScrollToTop$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.StartScrollToTop),
		switchMap(action => scrollToTop(action.element).pipe(
			map(_ => featureActions.FinishScrollToTop())
		)),
	));

	onNavigate$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Navigate),
		tap(action => {
			this.router.navigate(action.to);
		})
	), { dispatch: false });

	onNavigateBack$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NavigateBack),
		map(_ => {
			if (this.browserNav.hasPrevious) {
				window.history.back();
				return featureActions.Noop();
			} else {
				return featureActions.Navigate({
					to: [RoutesDefinitions.getBasePath()]
				});
			}
		})
	));

	loadUserOnFinishInit$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.Login),
		switchMap(_ => [
			// Put here post-user actions
			featureActions.LoadValidatorsRequest(),
			featureActions.LoadSalesConditionsRequest(),
			subsidiariesActions.LoadSubsidiariesRequest()
		])
	));

	openChoicesModal$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenChoicesModal),
		tap(action => {
			const modalRef = this.modalService.open(ChoicesModalComponent, { ...AppConstants.DEFAULT_MODAL_OPTIONS, ...action.options });
			modalRef.componentInstance.contentConfig = action.content;
			modalRef.componentInstance.choices = action.choices;
			from(modalRef.componentInstance.output).pipe(
				first(),
				catchError(_ => {
					modalRef.close();
					return EMPTY;
				}),
				map((output: IChoiceAction) => {
					modalRef.close();
					[].concat(output.do).forEach(a => this.store.dispatch(a));
				})
			).subscribe();
		})
	), { dispatch: false })

	openConfirmationModal$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenConfirmationModal),
		tap(action => {
			const modalRef = this.modalService.open(ConfirmModalComponent, { ...AppConstants.DEFAULT_MODAL_OPTIONS, ...action.options });
			modalRef.componentInstance.contentConfig = action.content;
			from(modalRef.result).pipe(
				first(),
				catchError(reason => {
					this.store.dispatch(featureActions.CloseConfirmationModal({
						actionType: reason, correlationParams: action.correlationParams
					}));
					if (reason === ActionType.CANCEL && action.cancelAction) {
						[].concat(action.cancelAction).forEach(a => this.store.dispatch(a));
					}
					return EMPTY;
				}),
				map(reason => {
					this.store.dispatch(featureActions.CloseConfirmationModal({
						actionType: reason, correlationParams: action.correlationParams
					}));
					if (reason === ActionType.CANCEL && action.cancelAction) {
						[].concat(action.cancelAction).forEach(a => this.store.dispatch(a));
					}
					if (reason === ActionType.VALIDATE && action.confirmationAction) {
						[].concat(action.confirmationAction).forEach(a => this.store.dispatch(a));
					}
				})
			).subscribe();
		})
	), { dispatch: false });

	toastsResetEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ToastReset),
		tap(_ => {
			this.notificationService.reset();
		})
	), { dispatch: false });

	toastEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ToastError, featureActions.ToastWarning, featureActions.ToastSuccess),
		tap(action => {
			this.notificationService.notify({
				type: action.level,
				message: action.message,
				messageParams: action.messageParams,
				options: action.options
			});
		})
	), { dispatch: false });

	NavigateToGateEffect = createEffect(() => this.actions$.pipe(
		ofType(featureActions.NavigateToGate),
		withLatestFrom(this.store.select(featureSelectors.selectConfig)),
		map(([action, config]) => {
			const language = this.languageService.getCurrentLanguage()
			window.location.href = `${config.authenticationServerUrl}${action.path}&culture=${language}`;
		})
	),{ dispatch: false });

	// LoadUserRequest
	loadUserRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadUserRequest),
		switchMap(_ => this.userService.getMe().pipe(
			map(user => featureActions.LoadUserSuccess({ entity: user }),
				catchError(error => of(featureActions.LoadUserFailure({ error })))
			))
		)));

	openDocument$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenDocument),
		switchMap(action => {
			// local file, or URL already fetched
			if (action.document.url && !!action.document.url['changingThisBreaksApplicationSecurity']) {
				return of(featureActions.OpenUrl({
					url: action.document.url['changingThisBreaksApplicationSecurity'],
					fileName: action.document.name
				}));
				// a document with an an id
			} else if (action.document.id) {
				return this.documentFileService.getLocalFileURL(action.document).pipe(
					map(document => featureActions.OpenUrl({
						url: document.url['changingThisBreaksApplicationSecurity'],
						fileName: action.document.name
					})),
					catchError(error => of(featureActions.ErrorLog(error)))
				);
				// unhandled
			} else {
				return of(featureActions.ErrorLog(`Cannot open document ${JSON.stringify(document)}`));
			}
		})
	));

	openLink$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenUrl),
		tap(action => {
			const a = document.createElement('a');
			a.setAttribute('href', action.url);
			a.setAttribute('target', '_blank');
			if (action.download) {
				a.setAttribute('download', action.fileName);
			}
			a.click();
		}),
		catchError(error => {
			// the browser probably blocked our window to open
			error.message = 'Cannot open window. ' + error.message;
			return of(featureActions.ErrorLog(error));
		})
	), {dispatch: false});

	printDocument$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PrintDocument),
		mergeMap(action => {
			// local file, or URL already fetched
			if (action.document.url && !!action.document.url['changingThisBreaksApplicationSecurity']) {
				return of(featureActions.PrintUrl(action.document.url['changingThisBreaksApplicationSecurity']));
				// a document with an an id
			} else if (action.document.id) {
				return this.documentFileService.getLocalFileURL(action.document).pipe(
					map(document => featureActions.PrintUrl({url: document.url['changingThisBreaksApplicationSecurity']})),
					catchError(error => of(featureActions.ErrorLog(error)))
				);
				// unhandled
			} else {
				return of(featureActions.ErrorLog(`Cannot print document ${JSON.stringify(document)}`));
			}
		})
	));

	printURL$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PrintUrl),
		tap(action => {
			window.open(action.url, '_blank').print();
		}),
		catchError(error => {
			// the browser probably blocked our window to open
			error.message = 'Cannot open print window. ' + error.message;
			return of(featureActions.ErrorLog(error));
		})
	), {dispatch: false});

	onOpenDocument$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDocument),
		tap(action => {
			const modalRef = this.modalService.open(DocumentFilesViewerComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
			modalRef.componentInstance.singleItem = action.document;
			modalRef.componentInstance.title = action.title ? this.translateService.instant(action.title) : null;
		})
	), { dispatch: false });

	loadSalesConditions$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSalesConditionsRequest),
		switchMap(_ => this.documentFileService.getLatestSalesConditionsDocument().pipe(
			map(entity => featureActions.LoadSalesConditionsSuccess({entity})),
			catchError(error => of(featureActions.LoadSalesConditionsFailure({ error })))
		)),
	));
}
